/**
 * Plant-quantity display helpers.
 *
 * Per-plant quantities come from the generation pipeline's vision count of the
 * generated image (Gemini), keyed back to each selected plant. They are an
 * ESTIMATE the user adjusts in the cart — never silently billed.
 */

/**
 * Format a per-plant quantity for display in front of a plant. Returns a
 * concrete count when we have a sane positive number; otherwise
 * "Count unavailable".
 *
 * The unknown case means the vision counter returned no usable count for this
 * plant. Never show a number for it — a placeholder like "1+" reads as "1" to
 * users (PLA2-763). Callers render an explicit 0 as "Not used" themselves.
 */
export function formatPlantQuantity(qty: number | undefined | null): string {
  const n =
    typeof qty === "number" && Number.isFinite(qty) ? Math.round(qty) : 0;
  if (n >= 1) return `${n} ${n === 1 ? "plant" : "plants"}`;
  return "Count unavailable";
}

/**
 * The quantity the cart will be seeded with for a plant. An explicit 0 ("not
 * used") stays 0; a sane positive count is rounded; a missing / non-finite
 * count (the vision counter failed) defaults to 1. Use this everywhere a count
 * feeds the cart, live quote or subtotal so they all agree.
 */
export function effectivePlantQuantity(qty: number | undefined | null): number {
  if (qty === 0) return 0;
  if (typeof qty === "number" && Number.isFinite(qty) && qty >= 1) {
    return Math.round(qty);
  }
  return 1;
}

/** Shown when at least one plant has no count from the vision counter. */
export const UNKNOWN_COUNT_NOTICE =
  "We couldn't count the plants in this design. Each plant defaults to 1 in your cart — adjust quantities before checkout.";

/** Suitability disclaimer to show wherever computed quantities appear. */
export const QUANTITY_DISCLAIMER =
  "Quantities are estimated from your design. Adjust them in your cart. Final counts depend on mature plant size and site conditions.";
